import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, Shield, ShieldCheck, Info, Trash2 } from 'lucide-react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';

export const AccountManagementPage = () => {
    const navigate = useNavigate();
    const currentUser = auth.currentUser;

    const [loading, setLoading] = useState(true);
    const [isPrivate, setIsPrivate] = useState(false);
    const [toggling, setToggling] = useState(false);

    useEffect(() => {
        const fetchUserData = async () => {
            if (!currentUser) return;
            try {
                const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                if (userDoc.exists()) {
                    setIsPrivate(userDoc.data().isPrivate || false);
                }
            } catch (err) {
                console.error("Failed to load user data:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchUserData();
    }, [currentUser]);

    const handleTogglePrivate = async () => {
        if (!currentUser || toggling) return;
        
        const newValue = !isPrivate;
        setIsPrivate(newValue); // Optimistic UI update
        setToggling(true);
        
        try {
            await updateDoc(doc(db, 'users', currentUser.uid), {
                isPrivate: newValue
            });
        } catch (err) {
            console.error("Toggle error:", err);
            setIsPrivate(!newValue); // Revert on failure
            toast.error("Failed to update privacy settings. Please try again.");
        } finally {
            setToggling(false);
        }
    };

    if (loading) {
        return (
            <div className="flex-1 flex items-center justify-center min-h-screen bg-[#0a0a0a]">
                <div className="w-8 h-8 rounded-full border-t-2 border-[#0BD9E5] animate-spin" />
            </div>
        );
    }

    return (
        <main className="flex-1 w-full flex flex-col bg-transparent">
            {/* Header */}
            <div className="sticky top-0 bg-[#0a0a0a]/90 backdrop-blur-md z-40 border-b border-[#1a1a1a] flex items-center h-[60px] px-4">
                <button onClick={() => navigate('/settings')} className="sm:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors mr-2">
                    <ChevronLeft size={24} className="text-white" />
                </button>
                <h1 className="text-xl font-bold text-white">Account Management</h1>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-6 pb-20 sm:pb-0">
                
                {/* Reality Mode Private Account — Disabled until Reality mode is live
                <div className="bg-[#111] border border-[#222] rounded-2xl overflow-hidden mb-6">
                    <div className="p-4 sm:p-5 flex items-center justify-between">
                        <div className="flex items-center flex-1 mr-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${isPrivate ? 'bg-[#BD00FF]/20 text-[#BD00FF]' : 'bg-gray-800 text-gray-400'}`}>
                                {isPrivate ? <ShieldCheck size={20} /> : <Shield size={20} />}
                            </div>
                            <div>
                                <h3 className="text-white font-bold text-[16px]">Private Account</h3>
                                <p className="text-gray-400 text-[13px] mt-0.5 leading-tight">
                                    When your account is private, only people you approve can see your profile and posts.
                                </p>
                                <span className="inline-block text-[#0BD9E5] text-[12px] font-semibold mt-1">
                                    Applicable in Real Identity mode only. Shadow Mode is always anonymous.
                                </span>
                            </div>
                        </div>
                        
                        <button 
                            onClick={handleTogglePrivate}
                            disabled={toggling}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${isPrivate ? 'bg-[#0BD9E5]' : 'bg-gray-700'}`}
                        >
                            <span 
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isPrivate ? 'translate-x-6' : 'translate-x-1'}`}
                            />
                        </button>
                    </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-[#0BD9E5]/10 border border-[#0BD9E5]/20 rounded-xl mb-8">
                    <Info size={20} className="text-[#0BD9E5] shrink-0 mt-0.5" />
                    <p className="text-[#0BD9E5] text-sm leading-relaxed">
                        Private account settings apply strictly when posting or interacting in Real Identity mode. Your Shadow Mode confessions remain 100% anonymous.
                    </p>
                </div>
                */}

                {/* Danger Zone */}
                <h2 className="text-sm font-bold text-red-500/70 uppercase tracking-wider mb-3 px-2">Danger Zone</h2>
                <div className="bg-[#111] border border-red-500/20 rounded-xl overflow-hidden">
                    <button 
                        onClick={() => navigate('/settings/delete-account')}
                        className="w-full p-4 flex items-center justify-between hover:bg-red-500/10 transition-colors"
                    >
                        <div className="flex items-center">
                            <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center mr-4">
                                <Trash2 size={20} className="text-red-500" />
                            </div>
                            <div className="text-left">
                                <h3 className="text-red-500 font-bold text-[16px]">Delete Account</h3>
                                <p className="text-gray-500 text-[13px] mt-0.5 leading-tight">
                                    Permanently delete your account and all data
                                </p>
                            </div>
                        </div>
                        <ArrowLeft size={16} className="text-red-500/50 rotate-180" />
                    </button>
                </div>

            </div>
        </main>
    );
};

export default AccountManagementPage;
